import { useEffect } from "react";
import "@/App.css";
import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { useAuthStore } from "@/store";
import { getPathLanguage, stripLanguagePrefix, withLanguagePrefix } from "@/lib/i18n";

// Pages
import HomePage from "@/pages/HomePage";
import TemplatesPage from "@/pages/TemplatesPage";
import PricingPage from "@/pages/PricingPage";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import DashboardPage from "@/pages/DashboardPage";
import EditorPage from "@/pages/EditorPage";
import RenderPage from "@/pages/RenderPage";
import RendersListPage from "@/pages/RendersListPage";
import AccountPage from "@/pages/AccountPage";
import SubscriptionPage from "@/pages/SubscriptionPage";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminTemplates from "@/pages/admin/AdminTemplates";
import AdminTemplateEditor from "@/pages/admin/AdminTemplateEditor";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminUserStats from "@/pages/admin/AdminUserStats";
import PrivacyPolicyPage from "@/pages/PrivacyPolicyPage";
import TermsOfServicePage from "@/pages/TermsOfServicePage";
import LicenseAgreementPage from "@/pages/LicenseAgreementPage";
import CookieNotice from "@/components/CookieNotice";
import ClientReviewPage from "@/pages/ClientReviewPage";
import AutoTranslate from "@/components/AutoTranslate";
import LanguageSuggestionDialog from "@/components/LanguageSuggestionDialog";
import WhatsAppContact from "@/components/WhatsAppContact";

// Protected Route Component
const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { isAuthenticated, user } = useAuthStore();
  const location = useLocation();
  const language = getPathLanguage(location.pathname);
  const redirectPath = `${stripLanguagePrefix(location.pathname)}${location.search}${location.hash}`;

  if (!isAuthenticated) {
    return <Navigate to={withLanguagePrefix("/login", language)} replace state={{ redirect: redirectPath }} />;
  }

  if (adminOnly && user?.role !== "admin") {
    return <Navigate to={withLanguagePrefix("/dashboard", language)} replace />;
  }

  return children;
};

const LanguageRouteManager = () => {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const lang = getPathLanguage(location.pathname);
    if (lang === "en") {
      return;
    }

    const normalizeInternalLinks = () => {
      const anchors = document.querySelectorAll('a[href^="/"]');
      anchors.forEach((anchor) => {
        const href = anchor.getAttribute("href");
        if (!href || href.startsWith("/api") || href.startsWith("//") || href.startsWith("/fr/") || href.startsWith("/es/") || href === "/fr" || href === "/es") {
          return;
        }
        anchor.setAttribute("href", withLanguagePrefix(href, lang));
      });
    };

    const handleClick = (event) => {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
        return;
      }

      const anchor = event.target?.closest?.("a[href]");
      if (!anchor) {
        return;
      }

      if (anchor.target === "_blank" || anchor.hasAttribute("download")) {
        return;
      }

      const href = anchor.getAttribute("href");
      if (!href || !href.startsWith("/") || href.startsWith("/api")) {
        return;
      }

      const destination = withLanguagePrefix(href, lang);
      event.preventDefault();
      navigate(destination);
    };

    normalizeInternalLinks();
    document.addEventListener("click", handleClick, true);
    return () => document.removeEventListener("click", handleClick, true);
  }, [location.pathname, navigate]);

  return null;
};

const ScrollToTop = () => {
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const targetId = decodeURIComponent(location.hash.slice(1));
      const targetElement = document.getElementById(targetId);

      if (targetElement) {
        requestAnimationFrame(() => {
          targetElement.scrollIntoView({ block: "start" });
        });
        return;
      }
    }

    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [location.pathname, location.hash]);

  return null;
};

function AppRoutes() {
  return (
    <>
      <LanguageRouteManager />
      <ScrollToTop />
      <AutoTranslate />
      <LanguageSuggestionDialog />
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={<HomePage />} />
        <Route path="/fr" element={<HomePage />} />
        <Route path="/es" element={<HomePage />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/fr/pricing" element={<PricingPage />} />
        <Route path="/es/pricing" element={<PricingPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/fr/login" element={<LoginPage />} />
        <Route path="/es/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/fr/register" element={<RegisterPage />} />
        <Route path="/es/register" element={<RegisterPage />} />
        <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
        <Route path="/fr/privacy-policy" element={<PrivacyPolicyPage />} />
        <Route path="/es/privacy-policy" element={<PrivacyPolicyPage />} />
        <Route path="/terms-of-service" element={<TermsOfServicePage />} />
        <Route path="/fr/terms-of-service" element={<TermsOfServicePage />} />
        <Route path="/es/terms-of-service" element={<TermsOfServicePage />} />
        <Route path="/license-agreement" element={<LicenseAgreementPage />} />
        <Route path="/review/:slug" element={<ClientReviewPage />} />
        <Route path="/fr/review/:slug" element={<ClientReviewPage />} />
        <Route path="/es/review/:slug" element={<ClientReviewPage />} />
        <Route path="/fr/license-agreement" element={<LicenseAgreementPage />} />
        <Route path="/es/license-agreement" element={<LicenseAgreementPage />} />

        {/* Protected User Routes */}
        <Route path="/templates" element={<TemplatesPage />} />
        <Route path="/fr/templates" element={<TemplatesPage />} />
        <Route path="/es/templates" element={<TemplatesPage />} />
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <DashboardPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/fr/dashboard"
          element={
            <ProtectedRoute>
              <DashboardPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/es/dashboard"
          element={
            <ProtectedRoute>
              <DashboardPage />
            </ProtectedRoute>
          }
        />
        <Route path="/editor/:templateId" element={<EditorPage />} />
        <Route path="/fr/editor/:templateId" element={<EditorPage />} />
        <Route path="/es/editor/:templateId" element={<EditorPage />} />
        <Route
          path="/renders"
          element={
            <ProtectedRoute>
              <RendersListPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/fr/renders"
          element={
            <ProtectedRoute>
              <RendersListPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/es/renders"
          element={
            <ProtectedRoute>
              <RendersListPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/renders/:renderId"
          element={
            <ProtectedRoute>
              <RenderPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/fr/renders/:renderId"
          element={
            <ProtectedRoute>
              <RenderPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/es/renders/:renderId"
          element={
            <ProtectedRoute>
              <RenderPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/account"
          element={
            <ProtectedRoute>
              <AccountPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/fr/account"
          element={
            <ProtectedRoute>
              <AccountPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/es/account"
          element={
            <ProtectedRoute>
              <AccountPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/subscription"
          element={
            <ProtectedRoute>
              <SubscriptionPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/fr/subscription"
          element={
            <ProtectedRoute>
              <SubscriptionPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/es/subscription"
          element={
            <ProtectedRoute>
              <SubscriptionPage />
            </ProtectedRoute>
          }
        />

        {/* Admin Routes */}
        <Route
          path="/admin"
          element={
            <ProtectedRoute adminOnly>
              <AdminDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/templates"
          element={
            <ProtectedRoute adminOnly>
              <AdminTemplates />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/templates/new"
          element={
            <ProtectedRoute adminOnly>
              <AdminTemplateEditor />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/templates/:templateId/editor"
          element={
            <ProtectedRoute adminOnly>
              <AdminTemplateEditor />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/users"
          element={
            <ProtectedRoute adminOnly>
              <AdminUsers />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/user-stats"
          element={
            <ProtectedRoute adminOnly>
              <AdminUserStats />
            </ProtectedRoute>
          }
        />

        {/* Catch all */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <CookieNotice />
      <WhatsAppContact />
    </>
  );
}

function App() {
  const { checkAuth } = useAuthStore();

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  return (
    <div className="min-h-screen bg-background">
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </div>
  );
}

export default App;
